/**
 * Display-time de-duplication of campsite features.
 *
 * The same facility often reaches a listing from several sources — an ICON
 * feature, an operator's CUSTOM text, a TEXT tag and a SEARCH facet — and they
 * differ only by case or a plural "s" ("Dump Point" / "Dump point",
 * "Caravan Park" / "Caravan Parks"). The database keeps them all; this only
 * stops the page from printing the same thing twice.
 *
 * The key is deliberately simple: lowercase, trim, collapse whitespace, and
 * singularise the LAST word only. Different phrases stay different — "Hot
 * Showers" and "Toilets & hot showers" are both kept.
 */

export interface DedupableFeature {
  desc?: string | null;
  feature_type?: string | null;
  icon_src?: string | null;
  note?: string | null;
  season_start?: number | null;
  season_end?: number | null;
}

/** Singularise one word. Short words ("bus", "gas", "bbq") are left alone. */
function singular(word: string): string {
  if (word.length <= 3) return word;
  if (word.endsWith('ss')) return word; // "grass", "access"
  if (word.length > 4 && word.endsWith('ies')) return word.slice(0, -3) + 'y'; // facilities -> facility
  if (/(ch|sh|x|ss)es$/.test(word)) return word.slice(0, -2); // beaches -> beach, boxes -> box
  if (word.endsWith('s')) return word.slice(0, -1); // showers -> shower
  return word;
}

/** Comparison key for a feature label. Empty string means "no label". */
export function featureKey(desc?: string | null): string {
  const words = (desc || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  words[words.length - 1] = singular(words[words.length - 1]);
  return words.join(' ');
}

/** Lower is better: an ICON with artwork beats free text, which beats a search facet. */
function presentationRank(f: DedupableFeature): number {
  const t = (f.feature_type || '').toUpperCase();
  if (t === 'ICON') return f.icon_src ? 0 : 1;
  if (t === 'CUSTOM') return 2;
  if (t === 'TEXT') return 3;
  if (t === 'SEARCH') return 4;
  return 5;
}

/**
 * Collapse features whose labels share a {@link featureKey}. The best-presented
 * one wins (ICON with an icon > ICON > CUSTOM > TEXT > SEARCH; ties keep the
 * first seen) and takes the slot of the first occurrence, so order is stable.
 * A note or season held only by a dropped duplicate is carried onto the winner.
 * Features without a label are passed through untouched.
 */
export function dedupeFeatures<T extends DedupableFeature>(features?: T[] | null): T[] {
  if (!features || features.length === 0) return [];
  const out: T[] = [];
  const slot = new Map<string, number>();

  for (const f of features) {
    const key = featureKey(f.desc);
    if (!key) {
      out.push(f);
      continue;
    }
    const at = slot.get(key);
    if (at === undefined) {
      slot.set(key, out.length);
      out.push(f);
      continue;
    }
    const kept = out[at];
    const [winner, loser] = presentationRank(f) < presentationRank(kept) ? [f, kept] : [kept, f];
    const merged: T = { ...winner };
    if (!merged.note && loser.note) merged.note = loser.note;
    if (merged.season_start == null && merged.season_end == null && loser.season_start != null && loser.season_end != null) {
      merged.season_start = loser.season_start;
      merged.season_end = loser.season_end;
    }
    out[at] = merged;
  }
  return out;
}
